/**
 * Shared data-table chrome.
 *
 * The dense look the device and DPS tables already used, extracted so every
 * page renders the same table instead of copying the sx blocks and the
 * sticky-column handling. Pages own their data, columns and toolbar contents;
 * this owns how a table looks.
 */

import React from 'react';
import {
  Box,
  CircularProgress,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import { Search as SearchIcon } from '@mui/icons-material';
import { flexRender, type RowData, type Table as TanStackTable } from '@tanstack/react-table';
import type { AppFeatures } from './tableFeatures';

/** Sticky columns need a solid background (no alpha) or rows show through. */
const STICKY_BG = {
  head: { dark: '#2D2D2D', light: '#FAFAFA' },
  body: { dark: '#1A1A1A', light: '#FFFFFF' },
};

const stickySx =
  (position: 'left' | 'right', variant: 'head' | 'body') => (theme: Theme) => ({
    position: 'sticky' as const,
    ...(position === 'left' ? { left: 0 } : { right: 0 }),
    bgcolor:
      theme.palette.mode === 'dark' ? STICKY_BG[variant].dark : STICKY_BG[variant].light,
    zIndex: variant === 'head' ? 10 : 9,
    boxShadow:
      position === 'left' ? '2px 0 4px rgba(0,0,0,0.1)' : '-2px 0 4px rgba(0,0,0,0.1)',
  });

/** Sticky cells sit above the row, so the row's own hover/selected tint never
 *  reaches them. Re-apply it as a gradient layer over their solid background. */
const stickyRowStateSx = (theme: Theme) => {
  const solid = theme.palette.mode === 'dark' ? STICKY_BG.body.dark : STICKY_BG.body.light;
  const layer = (dark: string, light: string) => {
    const tint = theme.palette.mode === 'dark' ? dark : light;
    return {
      bgcolor: solid,
      backgroundImage: `linear-gradient(${tint}, ${tint})`,
    };
  };
  return {
    '&:hover .sticky-cell': layer('rgba(255, 255, 255, 0.08)', 'rgba(0, 0, 0, 0.04)'),
    '&.Mui-selected .sticky-cell': layer(
      'rgba(144, 202, 249, 0.16)',
      'rgba(25, 118, 210, 0.08)'
    ),
    '&:hover.Mui-selected .sticky-cell': layer(
      'rgba(144, 202, 249, 0.24)',
      'rgba(25, 118, 210, 0.12)'
    ),
  };
};

export interface DataTableProps<T extends RowData> {
  table: TanStackTable<AppFeatures, T>;
  /** Names the table for screen readers. */
  ariaLabel: string;
  isLoading?: boolean;
  emptyMessage?: string;
  minWidth?: number;
  /** Per-row styling. Used to dim rows that cannot be acted on. */
  getRowSx?: (row: T) => SystemStyleObject<Theme>;
}

export function DataTable<T extends RowData>({
  table,
  ariaLabel,
  isLoading = false,
  emptyMessage = 'No data found',
  minWidth = 750,
  getRowSx,
}: DataTableProps<T>) {
  const visibleColumns = table.getVisibleFlatColumns();
  // Hidden columns must not pad the colspan of the loading/empty row.
  const colSpan = visibleColumns.length;
  const rows = table.getRowModel().rows;
  // Tables without a sticky column need none of the layering below.
  const hasSticky = visibleColumns.some((column) => column.columnDef.meta?.sticky);

  return (
    <TableContainer>
      <Table
        size="small"
        aria-label={ariaLabel}
        sx={{
          minWidth,
          // Density lives here, once, rather than on each column's cells.
          '& .MuiTableCell-root': {
            py: 0.75,
            px: 1.5,
            fontSize: '0.875rem',
            whiteSpace: 'nowrap',
          },
        }}
      >
        <TableHead
          sx={{
            bgcolor: 'action.hover',
            '& .MuiTableCell-head': {
              color: 'text.secondary',
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'none',
              letterSpacing: 0,
              py: 1,
            },
          }}
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const sticky = header.column.columnDef.meta?.sticky;
                const canSort = header.column.getCanSort();
                return (
                  <TableCell
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    sx={(theme) => ({
                      cursor: canSort ? 'pointer' : 'default',
                      ...(sticky ? stickySx(sticky, 'head')(theme) : {}),
                    })}
                  >
                    {header.isPlaceholder ? null : canSort ? (
                      <TableSortLabel
                        active={header.column.getIsSorted() !== false}
                        direction={header.column.getIsSorted() === 'desc' ? 'desc' : 'asc'}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </TableSortLabel>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={colSpan} align="center" sx={{ height: 180 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={colSpan}
                align="center"
                sx={{ height: 180, color: 'text.secondary' }}
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow
                key={row.id}
                hover
                selected={row.getIsSelected()}
                sx={(theme) => ({
                  ...(hasSticky ? stickyRowStateSx(theme) : {}),
                  ...(getRowSx ? getRowSx(row.original) : {}),
                })}
              >
                {row.getVisibleCells().map((cell) => {
                  const sticky = cell.column.columnDef.meta?.sticky;
                  return (
                    <TableCell
                      key={cell.id}
                      className={sticky ? 'sticky-cell' : ''}
                      sx={(theme) => (sticky ? stickySx(sticky, 'body')(theme) : {})}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export interface DataTableToolbarProps {
  /** Above zero, the toolbar switches to the selection view. */
  selectedCount?: number;
  selectedActions?: React.ReactNode;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  searchAriaLabel?: string;
  /** Controls shown at the right when nothing is selected. */
  actions?: React.ReactNode;
}

export const DataTableToolbar: React.FC<DataTableToolbarProps> = ({
  selectedCount = 0,
  selectedActions,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  searchAriaLabel = 'Search',
  actions,
}) => (
  <Toolbar variant="dense" sx={{ pl: { sm: 2 }, pr: { xs: 1, sm: 1 }, gap: 1 }}>
    {selectedCount > 0 ? (
      <>
        <Typography
          variant="subtitle1"
          component="div"
          sx={{
            color: 'inherit',
            flex: '1 1 100%'
          }}>
          {selectedCount} selected
        </Typography>
        {selectedActions}
      </>
    ) : (
      <>
        {/* Pushes the controls right. A spacer rather than ml:auto on the
            search box, so tables without a search still align their actions. */}
        <Box sx={{ flex: '1 1 auto' }} />
        {onSearchChange && (
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            aria-label={searchAriaLabel}
            value={searchValue ?? ''}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{ width: 260 }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }
            }}
          />
        )}
        {actions}
      </>
    )}
  </Toolbar>
);

export interface DataTablePaginationProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  rowsPerPageOptions?: number[];
}

export const DataTablePagination: React.FC<DataTablePaginationProps> = ({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [10, 25, 50, 100],
}) => (
  <TablePagination
    rowsPerPageOptions={rowsPerPageOptions}
    component="div"
    count={count}
    rowsPerPage={rowsPerPage}
    page={page}
    onPageChange={(_, newPage) => onPageChange(newPage)}
    onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
    sx={{
      borderTop: 1,
      borderColor: 'divider',
      '& .MuiToolbar-root': { minHeight: 44 },
      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
        fontSize: '0.75rem',
      },
    }}
  />
);

export default DataTable;
